import { Form } from "react-final-form";
import { useDispatch } from "react-redux";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import { FoldersApi } from "../api/folders";
import {
  addSubFolderToLogbook,
  updateLogbookSubFolder,
} from "../store/features/logbooks.slice";
import { addSubFolderToTemplate } from "../store/features/templates.slice";
import TextField from "./form/text-field";
import { requiredValidator } from "./form/validators";
import { handleError } from "./helpers";
import Modal from "./modal";
import Toast from "./toast";

/**
 * @typedef {Object} AddOrEditSubFolderModalProps
 * @property {inndox.SubFolder} [subFolder]
 * @property {number} folderId
 * @property {number} logbookId
 * @property {boolean} isVisible
 * @property {Function} onClose
 * @property {boolean} [isPartOfTemplate]
 */

/**
 *
 * @param {AddOrEditSubFolderModalProps} props
 * @returns
 */
export default function AddOrEditSubFolderModal({
  subFolder,
  folderId,
  isVisible,
  logbookId,
  onClose,
  isPartOfTemplate,
}) {
  const isEditingSubFolder = !!subFolder;
  const dispatch = useDispatch();

  return (
    <Modal
      isOpen={isVisible}
      title={isEditingSubFolder ? "Edit Folder" : "New Folder"}
      onClose={onClose}
    >
      <Form
        initialValues={{
          folderId,
          logbookId,
          ...subFolder,
        }}
        onSubmit={async ({ logbookId, folderId, ...remainingAttributes }) => {
          try {
            if (isEditingSubFolder) {
              const updatedSubFolder = await FoldersApi.updateSubFolder(
                logbookId,
                folderId,
                remainingAttributes.id,
                remainingAttributes,
              );

              dispatch(
                updateLogbookSubFolder({ ...updatedSubFolder, folderId }),
              );
            } else {
              const createdSubFolder = await FoldersApi.createNewSubFolder(
                logbookId,
                folderId,
                {
                  propertyId: logbookId,
                  folderId,
                  ...remainingAttributes,
                },
              );

              if (isPartOfTemplate) {
                dispatch(
                  addSubFolderToTemplate({
                    ...createdSubFolder,
                    folderId,
                  }),
                );
              } else {
                dispatch(
                  addSubFolderToLogbook({
                    ...createdSubFolder,
                    folderId,
                  }),
                );
              }
            }

            toast.success(
              <Toast
                title="Success!"
                details="Your sub-folder has been created."
              />,
            );

            onClose();
          } catch (e) {
            handleError(e);
          }
        }}
        render={(props) => (
          <>
            <p className="margin-top-1 margin-bottom-2">
              {isEditingSubFolder
                ? "Update folder details"
                : "Easily manage your documents by adding a new folder below."}
            </p>

            <TextField
              name="name"
              label="Name"
              required
              validate={requiredValidator}
              className="margin-top-2"
            />

            <TextField
              name="description"
              label="Description"
              className="margin-top-2"
            />

            <div className="flex margin-top-4">
              <button
                className="button button-primary button-large button-big"
                style={{ minWidth: 123 }}
                onClick={props.submitting ? undefined : props.handleSubmit}
              >
                {props.submitting ? (
                  <ClipLoader loading size={16} color="#fff" />
                ) : isEditingSubFolder ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </button>

              <button
                className="button button-secondary-link button-big margin-left-1"
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      />
    </Modal>
  );
}
